import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  Link as LinkIcon,
  SupportAgent as SupportIcon,
  SwapHoriz as ReassignIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { crm, copyText, fmtDateTime, fromNow } from '../../api/crm';

const TICKET_PRIORITIES = ['low', 'medium', 'high', 'urgent'];
const statusColor = { open: 'warning', in_progress: 'info', waiting_customer: 'default', resolved: 'success', closed: 'default' };

// ProfileActions: what a sales person can do from a customer's profile — see who owns
// the lead (leaders can reassign), send a payment link, resend the active links they
// generated for this customer, and raise / follow a support ticket.
const ProfileActions = ({ number, profile, onChanged }) => {
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [team, setTeam] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [ticket, setTicket] = useState({ subject: '', description: '', category: 'other', priority: 'medium' });
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignTo, setReassignTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const toast = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const name = profile?.obalgo_user?.name || '';

  const loadLinks = useCallback(() => {
    if (!profile?.can_payment_link) return;
    crm('payment-links/active', { query: { customer: number, limit: 10 } }).then((d) => setLinks(d?.data || [])).catch(() => setLinks([]));
  }, [number, profile?.can_payment_link]);

  const loadTickets = useCallback(() => {
    crm('support-tickets', { query: { number } })
      .then((d) => { setTickets(d?.tickets || []); setCategories(d?.categories || []); })
      .catch(() => setTickets([]));
  }, [number]);

  useEffect(() => { loadLinks(); loadTickets(); }, [loadLinks, loadTickets]);

  useEffect(() => {
    if (!profile?.can_reassign) return;
    crm('owner-access/me').then((me) => { setTeam((me.team || []).filter((m) => m.active)); setIsAdmin(me.role === 'admin'); }).catch(() => {});
  }, [profile?.can_reassign]);

  const copy = async (link) => {
    const ok = await copyText(link);
    toast(ok ? 'Link copied' : 'Could not copy. Long-press the link to copy it.', ok ? 'success' : 'warning');
  };

  const raiseTicket = async () => {
    setBusy(true);
    try {
      const res = await crm('support-tickets', { method: 'POST', body: { number, name, ...ticket } });
      toast(`Ticket ${res.ticket_id} raised`);
      setTicketOpen(false);
      setTicket({ subject: '', description: '', category: 'other', priority: 'medium' });
      loadTickets();
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const reassign = async () => {
    setBusy(true);
    try {
      await crm('leads/reassign', { method: 'POST', body: { numbers: [number], to: reassignTo } });
      toast(`Assigned to ${reassignTo || 'nobody'}`);
      setReassignOpen(false);
      if (onChanged) onChanged();
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">Assigned to</Typography>
        <Chip size="small" color={profile?.owner ? 'primary' : 'default'} label={profile?.owner || 'Unassigned'} />
        {profile?.can_reassign && (
          <Button size="small" startIcon={<ReassignIcon />} onClick={() => { setReassignTo(''); setReassignOpen(true); }} sx={{ textTransform: 'none' }}>Reassign</Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {profile?.can_payment_link && (
          <Button
            size="small"
            variant="contained"
            startIcon={<LinkIcon />}
            onClick={() => navigate(`/sales?tab=payment-links&number=${number}${name ? `&name=${encodeURIComponent(name)}` : ''}`)}
            sx={{ textTransform: 'none' }}
          >
            Payment link
          </Button>
        )}
        {profile?.can_raise_ticket && (
          <Button size="small" variant="outlined" startIcon={<SupportIcon />} onClick={() => setTicketOpen(true)} sx={{ textTransform: 'none' }}>Raise support ticket</Button>
        )}
      </Box>

      {links.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="subtitle2">Active payment links</Typography>
          {links.map((l) => (
            <Box key={l.transaction_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 160 }}>
                ₹{Math.round(l.amount).toLocaleString('en-IN')} · {l.plan} · {l.access_months} mo
                <Typography component="span" variant="caption" color="warning.main"> · expires {fromNow(l.link_expires_at)}</Typography>
              </Typography>
              <IconButton size="small" onClick={() => copy(l.link_url)} title="Copy link"><ContentCopyIcon fontSize="small" /></IconButton>
              <IconButton size="small" component="a" href={`https://wa.me/91${number}?text=${encodeURIComponent(`Here is your payment link: ${l.link_url}`)}`} target="_blank" rel="noopener noreferrer" sx={{ color: '#25D366' }} title="Send on WhatsApp"><WhatsAppIcon fontSize="small" /></IconButton>
            </Box>
          ))}
        </Box>
      )}

      {tickets.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="subtitle2">Support tickets</Typography>
          {tickets.map((t) => (
            <Box key={t.ticket_id} sx={{ py: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip size="small" color={statusColor[t.status] || 'default'} label={String(t.status || '').replace(/_/g, ' ')} sx={{ height: 20 }} />
                <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0, wordBreak: 'break-word' }}>{t.subject}</Typography>
                <Typography variant="caption" color="text.secondary">{t.ticket_id} · {t.source} · {fmtDateTime(t.created_at)}</Typography>
              </Box>
              {t.resolution && <Typography variant="caption" color="text.secondary" component="div">Resolution: {t.resolution}</Typography>}
            </Box>
          ))}
        </Box>
      )}

      <Dialog open={ticketOpen} onClose={() => setTicketOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Raise a support ticket · {number}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="What is the issue?" value={ticket.subject} onChange={(e) => setTicket({ ...ticket, subject: e.target.value })} autoFocus />
            <TextField label="Details for the support team" multiline minRows={3} value={ticket.description} onChange={(e) => setTicket({ ...ticket, description: e.target.value })} />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField select label="Category" value={ticket.category} onChange={(e) => setTicket({ ...ticket, category: e.target.value })}>
                {(categories.length ? categories : ['other']).map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
              <TextField select label="Priority" value={ticket.priority} onChange={(e) => setTicket({ ...ticket, priority: e.target.value })}>
                {TICKET_PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
            </Box>
            <Alert severity="info" sx={{ py: 0 }}>The ticket goes to the support team's queue. Its status will show here.</Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTicketOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={raiseTicket} disabled={busy || !ticket.subject.trim()}>Raise ticket</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reassignOpen} onClose={() => setReassignOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Reassign {number}</DialogTitle>
        <DialogContent>
          <TextField select fullWidth label="Assign to" value={reassignTo} onChange={(e) => setReassignTo(e.target.value)} sx={{ mt: 1 }}>
            {isAdmin && <MenuItem value="">Nobody (unassign)</MenuItem>}
            {team.map((m) => <MenuItem key={m.owner} value={m.owner}>{m.owner}{m.leader ? ' (leader)' : ''}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReassignOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={reassign} disabled={busy || (!reassignTo && !isAdmin)}>Reassign</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Paper>
  );
};

export default ProfileActions;
